/**
 * WebVTT ayrıştırıcı — altyazı bindirmesi için.
 *
 * ## Neden ayrıştırıyoruz?
 *
 * Tarayıcının `<track>` desteği **yalnız doğrudan oynatmada** çalışır
 * (`<video src="dosya.mkv">`). HLS/hls.js yolu MediaSource API (MSE) kullanır
 * ve MSE ile oynatmada `<track>` etiketleri **sessizce yoksayılır**.
 *
 * Gerçek sunucuda: 40+ yumuşak altyazılı bir film transcode edildi, kullanıcı
 * iz seçti, listede de göründü — ama ekranda **hiçbir şey çıkmadı**. Çünkü
 * altyazı `<track>` ile ekleniyordu ve yol MSE'ydi.
 *
 * Çözüm: sunucunun zaten ürettiği WebVTT'yi **çekip oynatıcının üstüne
 * bindirmek** (`overlay`). Bu yol MSE ile de, doğrudan oynatmada da aynı
 * şekilde çalışır — iki yol arasında davranış farkı kalmaz.
 *
 * Zamanlama `timeupdate` olayıyla (~4 Hz) güncellenir; atlamada anında yeniden
 * hesaplanır.
 */

export interface VttCue {
  /** Başlangıç, saniye. */
  start: number;
  /** Bitiş, saniye. */
  end: number;
  /** Görüntülenecek metin (satırlar birleştirilmiş). */
  text: string;
}

/**
 * `HH:MM:SS.mmm` veya `MM:SS.mmm` biçimini saniyeye çevirir.
 * Ayrıştırılamayan metin `NaN` döndürür (cuit atlanır).
 */
export function parseVttTimestamp(text: string): number {
  const parts = text.trim().split(':');
  if (parts.length < 2 || parts.length > 3) return Number.NaN;

  const seconds = Number(parts[parts.length - 1]);
  if (!Number.isFinite(seconds)) return Number.NaN;

  if (parts.length === 3) {
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.NaN;
    return hours * 3600 + minutes * 60 + seconds;
  }

  const minutes = Number(parts[0]);
  if (!Number.isFinite(minutes)) return Number.NaN;
  return minutes * 60 + seconds;
}

/**
 * Cue metnindeki **işaretlemeden** düz metne iner.
 *
 * ## Neden gerekiyor?
 *
 * SDH (Sağır/Doygun İşitme) altyazılarında sahne dışı konuşmalar ve ses
 * efektleri **italik** yazılır ve kaynak dosyada `<i>…</i>` olarak durur.
 * FFmpeg bunları WebVTT'ye olduğu gibi geçirir. Bindirme ise metni React'e
 * **düz metin** olarak verdiği için ekranda şu çıkardı:
 *
 *     <i>Gölgeler Şatosu da o kalbin kalbidir.</i>
 *
 * Gerçek sunucuda görüldü, düzeltildi (altyazı geliyordu ama `<i>` harfleri
 * de görünüyordu).
 *
 * ## Neden ayrıştırma sırasında, render sırasında değil?
 *
 * Bir kez, merkezî yerde yapılır: `cueAt` her karede çağrıldığı için
 * temizlik orada yapılırsa saniyede dört kez gereksiz iş olur.
 *
 * ## Neden "düz metne indirme" (biçim korunarak değil)?
 *
 * İki neden:
 *   1. **Güvenlik.** İçerik dışarıdan gelen bir dosyadan okunuyor. İşaretleme
 *      korunsaydı ya `dangerouslySetInnerHTML` gerekirdi (XSS yüzeyi) ya da
 *      her etiketi ayrı ayrı React öğesine çevirmenin getireceği karmaşıklık
 *      (`<v Speaker>` sınıfları, `<c.yellow>` stilleri, `<rt>` sesli kanji…)
 *      altyazı okunurluğunu artırmadan katlanılamayacak bir maliyet.
 *   2. **Tutarlılık.** Uygulamanın renk/yazı tipi tercihleri zaten var;
 *      kaynaktan gelen italik, bu tercihlerin üstüne yazardı.
 *
 * Sonuç: `<i>` **harfleri ekranda görünmez**, metin kendisi aynen kalır.
 *
 * Desteklenenler:
 *   - `<i>`, `<b>`, `<u>`, `<br>`, `<c.x>`, `<v Konuşmacı>`, `<ruby>`, `<rt>`
 *     ve kapanışları → **kaldırılır** (`<br>` → satır sonu)
 *   - `<00:00:01.000>` zaman damgası etiketleri → kaldırılır
 *   - `{\an8}`, `{\i1}`, `{\pos(0,0)}` ASS geçersiz kılma blokları → kaldırılır
 *   - `&amp; &lt; &gt; &quot; &apos; &nbsp;` ve sayısal kodlamalar → **çözülür**
 *
 * Çıplak `<` korunur (`5 < 10` gibi metinler bozulmaz): etiket deseni bir
 * harfle başlamak zorundadır.
 */
export function stripCueMarkup(input: string): string {
  return (
    input
      // 1) ASS geçersiz kılma blokları: `{\an8}`, `{\i1}`, `{\pos(1,2)}`.
      .replace(/\{\\[^{}]*\}/g, '')
      // 2) Satır sonu anlamına gelen etiketler **önce** çevrilir; genel
      //    etiket silme bunları yutardı.
      .replace(/<br\s*\/?>/gi, '\n')
      // 3) Zaman damgası etiketleri (WebVTT geçici etiketleri).
      .replace(/<\d{1,2}:\d{2}:\d{2}\.\d{3}>/g, '')
      // 4) Kalan etiketler: adla başlamak zorunda (çıplak `<` korunur).
      .replace(/<\/?[a-zA-Z][^>\n]{0,60}>/g, '')
      // 5) Kaçış dizileri çözülür — **etiketler temizlendikten sonra**, yoksa
      //    `&lt;i&gt;` yeniden etikete dönüşürdü.
      .replace(/&nbsp;/gi, ' ')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&apos;/gi, "'")
      .replace(/&#0*39;/g, "'")
      .replace(/&amp;/gi, '&')
      .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => safeCodePoint(Number.parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, dec: string) => safeCodePoint(Number.parseInt(dec, 10)))
  );
}

/**
 * Sayısal kodlamayı karaktere çevirir; geçersizse **hiçbir şey** üretmez.
 *
 * Altyazı dosyaları bozuk/ele geçmiş olabiliyor; `String.fromCodePoint`
 * geçersiz bir kod noktasında **RangeError** fırlatır ve bu, tek bir bozuk
 * etiketin tüm altyazı katmanını çökertmesi anlamına gelirdi.
 */
function safeCodePoint(code: number): string {
  if (!Number.isInteger(code) || code < 0 || code > 0x10ffff) return '';
  try {
    return String.fromCodePoint(code);
  } catch {
    return '';
  }
}

/**
 * WebVTT metnini cue listesine çevirir.
 *
 * Gözden geçirilenler:
 *   - `WEBVTT` başlığı ve `NOTE` blokları atlanır,
 *   - cue tanımlayıcısı (isteğe bağlı numara satırı) atlanır,
 *   - çok satırlı metinler `\n` ile birleştirilir,
 *   - WebVTT satır sonları `\n`; bazı üreticiler `\r\n` yazar — ikisi de kabul,
 *   - cue metnindeki işaretleme **düz metne** indirilir (`stripCueMarkup`).
 *
 * Saf fonksiyondur; test edilebilmesi için ağdan ve dosyadan bağımsızdır.
 */
export function parseVtt(input: string): VttCue[] {
  const cues: VttCue[] = [];
  // Önce satır sonlarını normalize et, sonra boş satırlarda blok böl.
  const blocks = input.replace(/\r\n?/g, '\n').split(/\n{2,}/);

  for (const block of blocks) {
    const lines = block.split('\n').filter((line) => line.trim().length > 0);
    if (lines.length === 0) continue;

    const head = lines[0]?.trim() ?? '';
    if (head.startsWith('WEBVTT') || head.startsWith('NOTE')) continue;

    // Zaman damgası satırını bul: `start --> end` (isteğe bağlı ayarlar olabilir).
    const timeIndex = lines.findIndex((line) => line.includes('-->'));
    if (timeIndex < 0) continue;

    const [rawStart, rawRest] = (lines[timeIndex] ?? '').split('-->');
    if (rawStart === undefined || rawRest === undefined) continue;

    // Bitiş damgasından CSS ayarları (`align:start position:50%`) ayrılır.
    const rawEnd = rawRest.trim().split(/\s+/)[0] ?? '';
    const start = parseVttTimestamp(rawStart);
    const end = parseVttTimestamp(rawEnd);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;

    const rawText = lines
      .slice(timeIndex + 1)
      .join('\n')
      .trim();
    // İşaretleme **ayrıştırma sırasında** temizlenir: `cueAt` oynatma sırasında
    // saniyede birkaç kez çağrılır, temizlik orada yapılırsa gereksiz iş olur.
    const text = stripCueMarkup(rawText).trim();
    if (text.length === 0) continue;

    cues.push({ start, end, text });
  }

  return cues;
}

/**
 * `t` anına denk gelen cue'u döndürür; yoksa `null`.
 *
 * Cue'lar başlangıç sırasına göre aranır (küçük dosyalarda doğrusal tarama
 * yeterli; 40 cue için maliyet ihmal edilebilir).
 */
export function cueAt(cues: VttCue[], t: number): VttCue | null {
  for (const cue of cues) {
    if (t >= cue.start && t < cue.end) return cue;
  }
  return null;
}
